/**
 * SIN — Meta _fbp / _fbc capture  (added 2026-07)
 *
 * WHY THIS EXISTS
 *   At the Meta CAPI cutover we turn OFF the Shopify Facebook app's data sharing.
 *   That app is what currently loads Meta's fbevents.js on the storefront, which
 *   is what creates the `_fbp` (browser ID) and `_fbc` (click ID) cookies. Our
 *   replacement custom pixel DOES load fbevents.js, but inside Shopify's pixel
 *   SANDBOX, which has a SEPARATE cookie jar — proven by observation on
 *   2026-07-20 (sandbox fbp = fb.1.1784598019214… vs storefront _fbp cookie =
 *   fb.1.1784596080199… on the same session).
 *
 *   So after cutover, nothing would create _fbp/_fbc on virginiasin.com at all.
 *   Meta weights both heavily for Event Match Quality, and our server-side
 *   Purchase/TradePurchase events currently send neither. This script closes
 *   that gap BEFORE cutover so EMQ is measured against a like-for-like setup.
 *
 * WHAT IT DOES
 *   1. Ensures a first-party `_fbp` cookie exists (creates one in Meta's format
 *      if absent — we can't rely on anything else creating it post-cutover).
 *   2. Derives `_fbc` from an `fbclid` URL parameter when present (Meta's format),
 *      and persists it.
 *   3. Stamps both onto the CART as cart attributes, so they land on the order as
 *      note_attributes, where the Cloudflare Worker reads them and adds them to
 *      the CAPI event's user_data.
 *
 * HOW IT WRITES TO THE CART — IMPORTANT
 *   It calls the theme's OWN method: Alpine.store('main').updateCartAttributes().
 *   That method (assets/base.js) merges { ...existing, ...new } rather than
 *   replacing, AND calls Rebuy.Cart.setCart() afterwards to keep Rebuy's cart
 *   state in sync. Writing to /cart/update.js directly would bypass both and
 *   could desync Rebuy. DO NOT "simplify" this to a raw fetch.
 *
 * GUARD
 *   Writes AT MOST ONCE per session: it compares against the LIVE cart state
 *   (Alpine.store('main').cart.attributes), not a JS flag, so it stays correct
 *   if the cart is cleared or the session does something unexpected.
 *
 * ISOLATION
 *   Deliberately a SEPARATE file from preserve-query-params.js. That file is
 *   load-bearing for ShopMy creator attribution; a Meta change must never be
 *   able to break it. Separate file = separate failure domain.
 *
 * TO REVERT
 *   Remove the one <script> line from layout/theme.liquid. Nothing else depends
 *   on this. Cart attributes already written are harmless.
 */
(function () {
  'use strict';

  var FBP_NAME = '_fbp';
  var FBC_NAME = '_fbc';
  var ATTR_FBP = '_fbp';   // cart attribute keys (underscore-prefixed keeps them
  var ATTR_FBC = '_fbc';   // out of customer-facing order displays)
  var COOKIE_DAYS = 90;    // Meta's own cookies use 90 days

  /* ---------- cookie helpers (storefront context — document.cookie works
     here; it is only the PIXEL SANDBOX where it does not) ---------- */

  function getCookie(name) {
    try {
      var parts = document.cookie ? document.cookie.split(';') : [];
      for (var i = 0; i < parts.length; i++) {
        var p = parts[i].trim();
        if (p.indexOf(name + '=') === 0) {
          return decodeURIComponent(p.substring(name.length + 1));
        }
      }
    } catch (e) {}
    return null;
  }

  function setCookie(name, value, days) {
    try {
      var d = new Date();
      d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
      // Root domain so the value is shared across www / apex, matching Meta's own behaviour.
      var host = window.location.hostname;
      var parts = host.split('.');
      var domain = parts.length > 2 ? '.' + parts.slice(-2).join('.') : '.' + host;
      document.cookie = name + '=' + encodeURIComponent(value) +
        ';expires=' + d.toUTCString() +
        ';path=/;domain=' + domain +
        ';SameSite=Lax;Secure';
    } catch (e) {}
  }

  /* ---------- Meta cookie formats ----------
     _fbp = fb.<subdomainIndex>.<creationTime>.<randomNumber>
     _fbc = fb.<subdomainIndex>.<creationTime>.<fbclid>
     subdomainIndex is 1 for a normal apex/www storefront. */

  function buildFbp() {
    var rand = Math.floor(Math.random() * 2147483647) + '' + Math.floor(Math.random() * 2147483647);
    return 'fb.1.' + Date.now() + '.' + rand;
  }

  function buildFbc(fbclid) {
    return 'fb.1.' + Date.now() + '.' + fbclid;
  }

  function getQueryParam(name) {
    try {
      return new URLSearchParams(window.location.search).get(name);
    } catch (e) {
      return null;
    }
  }

  /* ---------- ensure cookies exist ---------- */

  function ensureCookies() {
    var fbp = getCookie(FBP_NAME);
    if (!fbp) {
      fbp = buildFbp();
      setCookie(FBP_NAME, fbp, COOKIE_DAYS);
    }

    // _fbc: only (re)written when an fbclid is actually present on the URL.
    // A newer click must overwrite an older _fbc — that's Meta's own behaviour.
    var fbc = getCookie(FBC_NAME);
    var fbclid = getQueryParam('fbclid');
    if (fbclid) {
      fbc = buildFbc(fbclid);
      setCookie(FBC_NAME, fbc, COOKIE_DAYS);
    }

    return { fbp: fbp || null, fbc: fbc || null };
  }

  /* ---------- stamp onto the cart via the theme's own method ---------- */

  function stampCart(vals) {
    var store;
    try {
      store = window.Alpine && window.Alpine.store && window.Alpine.store('main');
    } catch (e) {
      return;
    }
    if (!store || typeof store.updateCartAttributes !== 'function') return;
    if (!store.cart) return;

    var existing = store.cart.attributes || {};
    var payload = {};

    if (vals.fbp && existing[ATTR_FBP] !== vals.fbp) payload[ATTR_FBP] = vals.fbp;
    if (vals.fbc && existing[ATTR_FBC] !== vals.fbc) payload[ATTR_FBC] = vals.fbc;

    // Guard: nothing changed -> no cart write at all.
    if (Object.keys(payload).length === 0) return;

    try {
      store.updateCartAttributes(payload);
    } catch (e) {}
  }

  /* ---------- run ---------- */

  function run() {
    var vals = ensureCookies();
    if (!vals.fbp && !vals.fbc) return;
    stampCart(vals);
  }

  // Alpine may not have initialised yet. alpine:init fires when it has; if Alpine
  // is already up (script loaded late), run immediately. Either way, run once.
  var hasRun = false;
  function runOnce() {
    if (hasRun) return;
    hasRun = true;
    run();
  }

  if (window.Alpine && window.Alpine.store) {
    runOnce();
  } else {
    document.addEventListener('alpine:init', function () {
      // alpine:init fires before stores are populated with cart data in some
      // cases; defer a tick so store('main').cart is available.
      setTimeout(runOnce, 0);
    });
    // Fallback in case alpine:init already fired before this script parsed.
    setTimeout(runOnce, 2000);
  }
})();
